import {
	formatDateForHash,
	getParticipantAges,
	getCompanyCookie,
	checkIfCompanyIsCgos,
	setHash,
} from "@/utils/utils"
import transportPackCode from "@/config/transportPackCode"
import flightPackCode, { isFlightEnabledLocale } from "@/config/flightPackCode"
import trainPackCode from "@/config/trainPackCode"
import { resolveTrainPackCodeForOrigin } from "@/config/trainPackageConfig"
import {
	cookie_setParticipantsNum,
	cookie_setParticipantsAges,
} from "@/logic/cookies"
import makeInitialAvailRequest from "@/vue/planning/logic/makeInitialAvailRequest"
import getCacheKey from "@/vue/planning/logic/getCacheKey"
import responsesCache from "@/vue/planning/state/responsesCache"
import searchPrestationsForFilter from "@/vue/planning/logic/searchPrestationsForFilter"
// import getPackageComposition from "./getPackageComposition"
import { isCS } from "@/vue/planning/logic/cs"
import { AvailabilitiesResponse } from "@/types/responses"
import store from "@/vue/planning/store/store"
import useLocale from "@/vue/composables/useLocale"

const locale = useLocale()

const getIsTranportPackCode = (packCode: string) => {
	if (transportPackCode.includes(packCode)) return true
	return false
}


const handleTransport = (state: any, commit: any, dispatch: any) => {
	if(isFlightEnabledLocale(locale)) {
		// Check if the new package is a flight pack and select the first available origin
		const isFlightPackage = flightPackCode.includes(state.packCode)
		const isTrainPackage = transportPackCode.includes(state.packCode)
		if (isFlightPackage) {
			store.dispatch("selectDefaultFlightOrigin", { overwrite: true, forceFlightPackCode: true })
		} else if (isTrainPackage) {
			store.dispatch("selectDefaultTrainOrigin", { overwrite: false, forceTrainPackCode: true, preferNative: true })
		}		
		commit("setTransportationSelected", isFlightPackage || isTrainPackage)
		return;
	}
	const isTranportPackCode = getIsTranportPackCode(state.packCode)
	if (isTranportPackCode) commit("setTransport", true)
	else commit("setTransport", false)
}

const handleCSE = (getters: any) => {
	if (getters.locale === "fr_FR_CSE" && checkIfCompanyIsCgos()) {
		// TODO Ido please externalize this piece of custom logic
		const participantAges = getParticipantAges()
		if (!participantAges) throw new Error("participantAges cannot be undefined")
		cookie_setParticipantsAges(participantAges)
		cookie_setParticipantsNum(participantAges.length)
	}
}

const handleWindowClickListener = (dispatch: any) => {
	const windowClickedListener = () => {
		dispatch("windowClicked")
	}
	try {
		document.body.removeEventListener("click", windowClickedListener)
	} catch (err) {
		console.error(err)
	}
	document.body.addEventListener("click", windowClickedListener)
}

const handleDates = (state: any, commit: any) => {
	const cacheKey = getCacheKey(
		state.packCode,
		state.dates["BeginDate"],
		state.dates["EndDate"],
		state.season,
		state.participants,
		state.skipass
	)
	const avail_resp = responsesCache.value.getResponse(
		"availabilities",
		cacheKey
	) as AvailabilitiesResponse

	if (
		avail_resp &&
		Object.keys(avail_resp).length > 0 &&
		(!state.dates["BeginDate"] || !state.dates["EndDate"])
	) {
		commit("setStartDate", formatDateForHash(avail_resp?.endDates?.startDate))
		commit("setEndDate", formatDateForHash(avail_resp?.endDates?.endDate))
	}
}

// honestly I hate the store so much, every bit of logic and state should be extracted using Composition API syntax
// #nukeVuex

const documentReady = async (store: any) => {
	const { state, getters, commit, dispatch } = store;

	state.firstLoad = true
	try {
		handleCSE(getters)

		commit("setInitialPrestationsMap") // create map from array on window

		dispatch("readCookies")
		dispatch("readUrl")
		dispatch("refreshStateFromHash")

		if (trainPackCode.includes(state.packCode)) {
			const urlOrigin = state.flightOrigin?.split("-")[0] || ""
			const resolvedFromOrigin = resolveTrainPackCodeForOrigin(state.packCode, urlOrigin)
			if (resolvedFromOrigin && resolvedFromOrigin !== state.packCode) {
				commit("setPackage", resolvedFromOrigin)
				setHash({ packCode: resolvedFromOrigin })
			}
			dispatch("selectDefaultTrainOrigin", {
				overwrite: false,
				forceTrainPackCode: true,
				// Keep URL origin when it already fits the (possibly remapped) pack.
				preferNative: !urlOrigin,
				packCode: state.packCode,
			})
		}

		if (window.seasonManager) {
			commit("setSeason", window.seasonManager.season)
		}

		handleWindowClickListener(dispatch)

		await makeInitialAvailRequest(store)
		commit("setLoading", false)

		if (state.productUnavailable) return;


		await searchPrestationsForFilter(store)

		handleDates(state, commit)

		dispatch("refreshStateFromHash")

		await dispatch("checkActivePrestations")

		// Ensure prestations are preselected from URL parameters after all data is loaded
		await dispatch("ensurePrestationsPreselected")
		
		// Handle transport after preselection to ensure consistency
		handleTransport(state, commit, dispatch)


		// Not anymore in use - use availability service instead
		// if (state.packCode !== "HS" && !state.packCode.endsWith("CS")) {
		// 	await getPackageComposition(store)
		// }

		window.addEventListener("refreshDataStoredOnWindow", () => {
			dispatch("refreshDataStoredOnWindow")
		})

		state.firstLoad = false
	} catch (err) {
		console.error("[product] documentReady failed", err)
	} finally {
		commit("setLoading", false)
	}
}

export default documentReady
